import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Grid, InputLabel, TextField, Tooltip, Typography } from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { getOnBoardCompliance, postAddOnBoardCompliance, getCompliance } from 'redux/actions/UserCompliance';
import { dateFormate, truncateComment } from 'utils/utils';
import { complianceObject, dateObject } from 'utils/enumObject';  
import Loader from 'components/loader/Loader';
import closebtn from "assets/images/CrossBtn.svg"
import warning from "assets/images/Warning.svg"
import greenCheck from "assets/images/CheckSquare.svg"
import { postReuploadCompliance } from 'services/ComplianceReuploadService';
import { toast } from 'react-toastify';

// Utility function to check if document is expiring soon (within 30 days)
const isSoonToExpire = (expiryDate) => {
  if (!expiryDate) return false;
  
  const currentDate = new Date();
  const expiry = new Date(expiryDate);
  const timeDifference = expiry.getTime() - currentDate.getTime();
  const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));
  
  return daysDifference > 0 && daysDifference <= 30;
};

// Add reupload prop to the component parameters
function ComplianceUpload(props) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const minDate = dateFormate(new Date(),'YYYY-MM-DD')
  const [image, setImage] = useState(null);
  const [close, setclose] = useState(true);
  const [formValidation, setFormValidation] = useState({});

  const onBoardComplianceData = useSelector((state) => state.userOnBoardCompliance?.userOnBoardCompliance);

  const complianceSource =
  props?.reupload && props?.details
    ? props.details
    : onBoardComplianceData?.data || [];

  const vehicledata = useSelector((state) => state.userAddVehicle?.userAddVehicle.data)
  
  // Updated filtering to include documents that need attention based on context
  const rejectedData = complianceSource?.filter((val, idx) => {
    if (props?.fromSubVendorExpiry) {
      // From SubVendor Compliance Expiry tab - include rejected, expired, and soon to expire
      return val?.complianceValue[0]?.status === 'Rejected' || 
             val?.complianceValue[0]?.isExpired === true ||
             isSoonToExpire(val?.complianceValue[0]?.expiryDate);
    } else {
      // From other places - only include rejected documents
      return val?.complianceValue[0]?.status === 'Rejected';
    }
  });
  
  const UploadedData = complianceSource?.filter((val, idx) => val?.complianceValue[0]?.status === 'Uploaded')
  
  const addOnBoardCompliance = useSelector((state) => state?.userAddOnBoardCompliance?.userAddOnBoardCompliance?.success)
  const addComplianceLoader = useSelector(
    (state) => state?.userOnBoardCompliance?.loading
  );
  const updateComplianceLoader = useSelector(
    (state) => state?.userAddOnBoardCompliance?.loading
  );
  const [licenseNumberEdited, setLicenseNumberEdited] = useState(false);
  
  const [formDataIntialise, setFormDataIntialise] = useState([{ licenseNumber: '', expiryDate: '', imageName: null  }]);

  const [fileSizeError, setFileSizeError] = useState(Array(formDataIntialise.length).fill(false));
  const [fileTypeError, setFileTypeError] = useState(Array(formDataIntialise.length).fill(false));

  const complianceData = complianceSource || [];

// Check if all mandatory items have 'Approved' status and are not expired or expiring soon
const allMandatoryApproved = complianceData
  .filter((item) => item.isMandatory) // Filter only mandatory items
  .every((item) => 
    item.complianceValue[0]?.status === 'Approved' && 
    !item.complianceValue[0]?.isExpired &&
    !isSoonToExpire(item.complianceValue[0]?.expiryDate)
  );

  useEffect(() => {
    if(props?.tabName !== 'vehicle' && props?.tabName !== 'driver'){
      dispatch(getOnBoardCompliance());
    }
    const statusCompliance = localStorage.getItem('status')
    if(statusCompliance === 'Active' && props?.tabName === 'Self'&& props?.tabName !== 'vehicle'){
      navigate('/dashboard')
    }
  }, []); 

  const fileSize = (file) => {
    const sizeInBytes = file.size;
    return sizeInBytes;
  };

  const handleFileChange = (event, idx) => {
    setFileSizeError('')
    setFileTypeError('')
    if (event.target.files && event.target.files.length > 0) {
      const files = event.target.files; // Get the array of selected files
      const attributeType = complianceSource[idx]?.attributeType;

      const allowedFileTypesForAttributeType = attributeType === 'File' 
      ? ['image/jpeg', 'image/png', 'application/pdf'] 
      : ['image/jpeg', 'image/png'];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!allowedFileTypesForAttributeType.includes(file.type)) {
          // Check if the file type is not allowed
          setFileTypeError((prevErrors) => {
            const updatedErrors = [...prevErrors];
            updatedErrors[idx] = true;
            return updatedErrors;
          });
          // Clear the imageName when a file type error occurs
          setFormDataIntialise((prevFormData) => {
            const updatedFormData = [...prevFormData];
            updatedFormData[idx] = { ...updatedFormData[idx], imageName: ' ' };
            return updatedFormData;
          });
  
          return; // Exit the function
        }

        if (fileSize(file) > complianceObject.fileSize) {
          setFileSizeError((prevErrors) => {
            const updatedErrors = [...prevErrors];
            updatedErrors[idx] = true;
            return updatedErrors;
          });
          setFormDataIntialise((prevFormData) => {
            const updatedFormData = [...prevFormData];
            updatedFormData[idx] = { ...updatedFormData[idx], imageName: ' ' };
            return updatedFormData;
          });
  
          return;
        }
      }

      setFormDataIntialise((prevFormData) => {
        const updatedFormData = [...prevFormData];
        const imageNames = [];
        for (let i = 0; i < files.length; i++) {
          imageNames.push(files[i].name);
        }
        updatedFormData[idx] = { ...updatedFormData[idx], imageName: imageNames.join(', ') };
        return updatedFormData;
      });
  
      // For handling only one file in 'File' attribute type
      if (complianceSource[idx]?.attributeType === 'File' && files.length === 1) {
        setImage(files[0]); // Store the selected file
      } else {
        setImage(files); // Store the selected files
      }
    }
  };

  const complainceClick = async (attributeId, idx) => {
    const filteredData = formDataIntialise[idx];
  
    // Create a new FormData instance
    const formDataDetail = new FormData();
    if (complianceSource[idx]?.attributeType === 'File') {
      formDataDetail.append('complianceFile', image || "");
    } 
    else if(complianceSource[idx]?.attributeType === 'Image'){
      formDataDetail.append('complianceFile', image[0] || "");
    }
    else if(complianceSource[idx]?.attributeType === 'Multi-Image') {
      for (let i = 0; i < image?.length; i++) {
        formDataDetail.append('complianceFile', image[i] || "");
      }
    }
     if (props?.reupload) {
    formDataDetail.append('complianceValueId', attributeId);
  } else {
    formDataDetail.append('complianceAttributeId', attributeId);
  }

    if (!filteredData?.licenseNumber) {
      const attributeValue = complianceSource[idx]?.complianceValue[0]?.atributeValue
      formDataDetail.append('complianceValue', attributeValue);
    } else {
      formDataDetail.append('complianceValue', filteredData?.licenseNumber);
    }

    let selectedVehicleId = ''
    if(props?.tabName === 'vehicle'){
      selectedVehicleId = props?.vehicleId || vehicledata[0]?.vehicleId;
    }
  if (selectedVehicleId ) {
    formDataDetail.append('vehicleId', selectedVehicleId );
  }
  if(props?.tabName === 'driver'){
    formDataDetail.append('driverId', props?.driverId );
  }
if (complianceSource?.[idx]?.isExpiring === true) {
  if (!filteredData?.expiryDate) {
    const expiryValue = complianceSource[idx]?.complianceValue[0]?.expiryDate || '';
    formDataDetail.append('expiryDate', expiryValue);
  } else {
    formDataDetail.append('expiryDate', filteredData?.expiryDate);
  }
}
try {

  if (props?.reupload) {

    const response = await postReuploadCompliance(formDataDetail);

    setImage(null);

    if (response?.success) {
      toast.success(response?.message || 'Compliance document reuploaded successfully');
      if (props?.onSuccess) {
        props.onSuccess();
      }
    } else {
      toast.error(response?.message || 'Reupload compliance failed');
    }

  } else {

    dispatch(postAddOnBoardCompliance(formDataDetail));

  }

} catch (err) {
  console.error("Error in compliance upload:", err);
  if (props?.reupload) {
    toast.error(err?.response?.data?.message || 'Reupload compliance failed');
  }
}
  };

  const handleInput = (e, idx, element) => {
    const { name, value } = e.target;
    if (name === 'licenseNumber') {
      setLicenseNumberEdited(true);
    }

    setFormDataIntialise((prevFormData) => {
      const updatedFormData = [...prevFormData];
      updatedFormData[idx] = { ...updatedFormData[idx], [name]: value};
      return updatedFormData;
    });

    if(element?.ComplianceValidation?.id) {
      const regexTemplate = new RegExp(element?.ComplianceValidation?.value) 
      let errorData = ""

      if(e.target.value) {
        if(!regexTemplate.exec(e.target?.value?.toUpperCase())) {
          errorData = {...formValidation, [element?.attributeName]: true, [`${element?.attributeName}-msg`]: element?.ComplianceValidation?.description}
        }
        else {
          errorData = {...formValidation, [element?.attributeName]: false}
       }
      }
      else {
        errorData = {...formValidation, [element?.attributeName]: false}
     }   
      setFormValidation(errorData);
    }

  };

  const isInputEmpty = (idx, formFieldError, element) => {
    const formData = formDataIntialise[idx];
    const imageType = element?.attributeType.toLowerCase() === "text" ? true : formData?.imageName;
    const expiryType = element?.isExpiring ? formData?.expiryDate : true;    

    if(formData?.licenseNumber && imageType && expiryType && !formFieldError) {
      return false
    }
    else {
      return true
    }
  };
  
  const handleclose = () => {
    setclose(false)
  }

  
  return (
    <div>
      {((addComplianceLoader || updateComplianceLoader) && (props.tabName !== 'vehicle')) ? 
        <div>
          <Loader />
        </div> :
        <Grid container md={12} direction='column' className='marginBlock10'>
          {/* Updated notifications section to handle different contexts */}
          {!props?.reupload && close && rejectedData?.length > 0 && 
            <Grid container direction='row' justifyContent='space-between' alignItems='center' className='backgroundRejected'>
              <Grid item xs={10}>
                {rejectedData?.map(val => {
                  const isSoonExpiring = isSoonToExpire(val?.complianceValue[0]?.expiryDate);
                  const isExpired = val?.complianceValue[0]?.isExpired;
                  const isRejected = val?.complianceValue[0]?.status === 'Rejected';
                  
                  let message = '';
                  if (props?.fromSubVendorExpiry) {
                    // Show all types of messages for SubVendor Compliance Expiry
                    if (isRejected) {
                      message = val?.complianceValue[0]?.rejectionReason;
                    } else if (isExpired) {
                      message = 'Expired';
                    } else if (isSoonExpiring) {
                      message = 'Soon to Expire - Please renew';
                    }
                  } else {
                    // Only show rejected messages for normal flow
                    if (isRejected) {
                      message = val?.complianceValue[0]?.rejectionReason;
                    }
                  }
                  
                  return (
                    <ul className='colorDarkRed marginBlock30' key={val.id}>
                      <li>{val.attributeName + ": " + message}</li>
                    </ul>
                  );
                })}
              </Grid>
              <Grid item xs={1} className='vendorIcons'>
                <img src={closebtn} alt='close' onClick={handleclose} className='cursor padding10'/>
              </Grid>
            </Grid>
          }
            
          {/* Updated status text section */}
          {!props?.reupload && 
            <Grid item>
              {allMandatoryApproved ? (
                <Typography className={`font18 fw400 colorGreen marginBottom25 ${props?.tabName === 'Self' && 'font28'} `}>
                  Your Compliances are verified
                </Typography>
              ) : rejectedData?.length > 0 ? ( 
                <Typography className={`font18 fw400 colorOrange marginBottom25 ${props?.tabName === 'Self' && 'font28'} `}>
                  Some documents need attention (rejected, expired, or expiring soon)
                </Typography>
              ) : UploadedData?.length > 0 ? ( 
                <Typography className={`font18 fw400 colorYellow marginBottom25 ${props?.tabName === 'Self' && 'font28'} `}>
                  Your Compliances is under verifications
                </Typography>
              ) : (
                <Typography className={`font18 fw400 colorGrey marginBottom25 ${props?.tabName === 'Self' &&  'font28'} `}>
                  You need to upload your Compliances as given below for verification
                </Typography>
              )}
            </Grid>
          }

          {complianceSource?.map((ele, idx) => {
            const isSoonExpiring = isSoonToExpire(ele?.complianceValue[0]?.expiryDate);
            const isExpired = ele?.complianceValue[0]?.isExpired;
            const isRejected = ele?.complianceValue[0]?.status === 'Rejected';
            const isApproved = ele?.complianceValue[0]?.status === 'Approved';
            
            // Determine if upload/edit should be allowed based on context
            let canEdit = true;
            // if (props?.fromSubVendorExpiry) {
            //   // From SubVendor Compliance Expiry tab - allow editing for rejected, expired, and soon to expire
            //   canEdit = isRejected || isExpired || isSoonExpiring;
            // } else {
            //   // From other places - only allow editing for rejected documents
            //   canEdit = isRejected;
            // }
            
            // Determine border color based on status and context
            let borderClass = '';
            if (isRejected) borderClass = 'borderRed';
            else if (isExpired) borderClass = 'borderRed';
            else if (isSoonExpiring && props?.fromSubVendorExpiry) borderClass = 'borderOrange';
            
            return (
              <Grid
                item
                container
                md={12}
                key={idx}
                direction='column'
                className={props.tabName === 'vehicle' ? 'complianceUploadModal' : 'complianceUploadModalForDriver'}
              >
                <Grid item className={`complianceColor ${borderClass}`}>
                  <Typography className='font18 colorBlue flexStart'>
                    {isRejected ? (
                      <img src={warning} alt='rejected' className='width20 marginRight6' />
                    ) : isExpired ? (
                      <img src={warning} alt='expired' className='width20 marginRight6' />
                    ) : (isSoonExpiring && props?.fromSubVendorExpiry) ? (
                      <img src={warning} alt='soon to expire' className='width20 marginRight6' />
                    ) : isApproved ? (
                      <img src={greenCheck} alt='approved' className='width20 marginRight6' />
                    ) : ""}
                    {ele?.attributeName} {ele?.isMandatory && <span className='colorRed'>*</span>}
                  </Typography>
                  <Grid
                    item
                    container
                    direction='row'
                    justifyContent='space-between'
                    alignItems='flex-end'
                    className='margin10'
                  >
                    {ele?.attributeType === 'File' ? (
                      <Grid item md={12} className='mrb10'>
                        {(!ele?.complianceValue[0]?.complianceFile?.name || canEdit) &&
                        <Button
                          variant="contained"
                          component="label"
                          className='uploadBtnCompliance'
                        >
                          <Typography>Upload File </Typography> <FileUploadOutlinedIcon />
                          <input
                            type="file"
                            hidden
                            accept="image/jpeg, image/png, application/pdf"
                            onChange={(event) => handleFileChange(event, idx)}
                          />
                        </Button>}
                        {formDataIntialise[idx]?.imageName && formDataIntialise[idx]?.imageName !== null ? ( 
                          <Typography className={(!ele?.complianceValue[0]?.complianceFile?.name || canEdit) ? 'font14' : 'font16'}>
                            <Tooltip title={formDataIntialise[idx].imageName} placement='bottom'>
                              {truncateComment(formDataIntialise[idx].imageName,20)}
                            </Tooltip>
                          </Typography>
                        ) : (
                          <Typography>
                            {ele?.complianceValue?.length > 0 && ele.complianceValue[0]?.complianceFile?.name
                                ? (<Tooltip title={ele.complianceValue[0].complianceFile.name} placement='bottom'>{truncateComment(ele.complianceValue[0].complianceFile.name,15)}</Tooltip>) : ""}
                          </Typography>
                        )}
                           {fileSizeError[idx] && (
                          <Typography className="colorRed">File size should not exceed 1 MB</Typography>
                        )}
                        {fileTypeError[idx] && (
                          <Typography className="colorRed">selected File Type Not Allowed</Typography>
                        )}
                      </Grid>
                    ) : null}
                    {ele?.attributeType === 'Image' ? (
                      <Grid item md={12} className='mrb10'>
                        {(!ele?.complianceValue[0]?.complianceFile?.name || canEdit) &&
                        <Button
                          variant="contained"
                          component="label"
                          className='uploadBtnCompliance'
                        >
                          <Typography>Upload File </Typography> <FileUploadOutlinedIcon />
                          <input
                            type="file"
                            hidden
                            accept="image/jpeg, image/png"
                            onChange={(event) => handleFileChange(event, idx)}
                          />
                        </Button>}
                        {formDataIntialise[idx]?.imageName && formDataIntialise[idx]?.imageName !== null ? ( // Check if file name is present
                          <Typography className='font14'>
                            <Tooltip title={formDataIntialise[idx].imageName} placement='bottom'>
                              {truncateComment(formDataIntialise[idx].imageName,20)}
                            </Tooltip> 
                          </Typography>
                        ) : 
                        (
                          <Typography>
                            {ele?.complianceValue?.length > 0 && ele.complianceValue[0]?.complianceFile?.name
                              ? (<Tooltip title={ele.complianceValue[0]?.complianceFile?.name} placement='bottom'>{truncateComment(ele.complianceValue[0]?.complianceFile?.name,25)}</Tooltip>) : ""}
                          </Typography>
                        )}
                           {fileSizeError[idx] && (
                          <Typography className="colorRed">File size should not exceed 1 MB</Typography>
                        )}
                        {fileTypeError[idx] && (
                          <Typography className="colorRed">selected File Type Not Allowed</Typography>
                        )}
                      </Grid>
                    ) : ele?.attributeType === 'Multi-Image' ? (
                      <Grid item md={12} className='mrb10'>
                        {(!ele?.complianceValue[0]?.complianceFile?.name || canEdit) &&
                        <Button
                          variant="contained"
                          component="label"
                          className='uploadBtnCompliance'
                        >
                          <Typography>Upload File </Typography> <FileUploadOutlinedIcon />
                          <input
                            type="file"
                            hidden
                            accept="image/jpeg, image/png"
                            multiple
                            onChange={(event) => handleFileChange(event, idx)}
                          />
                        </Button>}
                        {formDataIntialise[idx]?.imageName && formDataIntialise[idx]?.imageName !== null ? ( // Check if file name is present
                          <Typography className='font14'>
                            <Tooltip title={formDataIntialise[idx].imageName} placement='bottom'>
                              {truncateComment(formDataIntialise[idx].imageName,20)}
                            </Tooltip> 
                          </Typography>
                        ) : 
                        (
                          <Typography>
                            {ele?.complianceValue?.length > 0 && ele.complianceValue[0]?.complianceFile?.name
                              ? (ele?.complianceValue?.map((val,index) => (
                                <Tooltip key={index} title={val?.complianceFile?.name} placement='bottom'>
                                  {truncateComment(val?.complianceFile?.name,25)+", "}
                                </Tooltip>
                              ))) : ""}
                          </Typography>
                        )}
                           {fileSizeError[idx] && (
                          <Typography className="colorRed">File size should not exceed 1 MB</Typography>
                        )}
                        {fileTypeError[idx] && (
                          <Typography className="colorRed">selected File Type Not Allowed</Typography>
                        )}
                      </Grid>
                    ) : null}
                      <Grid item md={12} className='mrb10'>
                        <InputLabel htmlFor="expiry-date-input">{ele?.attributeType === 'File' && ele?.attributeName ? ele?.attributeName+' Number' : ele?.attributeName}</InputLabel>
                        <TextField
                          className='marginTop width100 bgWhite licenseTextField'
                          size='small'
                          type='text'
                          value={props.tabName === 'vehicle' 
                            ? (formDataIntialise[idx]?.licenseNumber || ele.complianceValue[0]?.atributeValue) || '' 
                            : licenseNumberEdited ? formDataIntialise[idx]?.licenseNumber : ele.complianceValue[0]?.atributeValue}
                          name='licenseNumber'
                          placeholder={`Enter ${ele?.attributeName}`}
                          onChange={(event) => handleInput(event, idx, ele)}
                          disabled={!canEdit}
                        />
                        {formValidation?.[ele?.attributeName] && <Typography className='errorMessage'>{formValidation?.[`${ele?.attributeName}-msg`]}</Typography>}
                      </Grid>
                    {ele?.isExpiring === true && (
                      <Grid item md={12} className='mrb10'>
                        <InputLabel htmlFor="expiry-date-input">Expiry Date</InputLabel>
                        <TextField
                          className='marginTop width100 bgWhite'
                          size='small'
                          type='date'
                          value={props.tabName === 'vehicle' 
                            ? (formDataIntialise[idx]?.expiryDate ||  ele.complianceValue[0] && dateFormate(ele.complianceValue[0]?.expiryDate,'YYYY-MM-DD'))|| ' '
                            : formDataIntialise[idx]?.expiryDate ||  ele.complianceValue[0] && dateFormate(ele.complianceValue[0]?.expiryDate,'YYYY-MM-DD')}
                          name='expiryDate'
                          onChange={(event) => handleInput(event, idx)}
                          InputProps={{inputProps: { min: minDate, max: dateObject.maxDate} }}
                          disabled={!canEdit}
                        >
                          Expiry Date
                        </TextField>
                      </Grid>
                    )}
                    {canEdit && (!isApproved || (props?.reupload && (isExpired || isRejected || isSoonExpiring))) && (
                      <Grid item className='mrb10'>
                        <Button 
                          className={`uploadComplianceBtn ${
                            !formValidation?.[ele?.attributeName] && 
                            !isInputEmpty(idx, formValidation?.[ele?.attributeName], ele) ? 
                            'buttonSubmit': 'buttonSubmitDisabled'}`}
                          disabled={isInputEmpty(idx, formValidation?.[ele?.attributeName], ele)} 
                          onClick={() => complainceClick(ele?.id,idx)}
                        >
                          {props?.reupload ? 'Reupload' : 'Save'}
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            )
          })}
        </Grid>
      }
    </div>
  );
}

export default ComplianceUpload;